import { ref, computed, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import moment from 'moment-timezone'
import store from '@/store'
import useFilterCounter from '@/components/Filters/useFiltersCount'
import { useUtils as useI18nUtils } from '@core/libs/i18n'
import { debounce } from 'lodash'

const isTimeOnlyString = (value) => {
  if (typeof value !== 'string') return false
  const trimmedValue = value.trim()
  if (!trimmedValue) return false
  return /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/.test(trimmedValue)
}

const formatTimeOnly = (value) => {
  if (!value) return '—'
  if (!isTimeOnlyString(value)) return '—'
  const parsedTime = moment(value.trim(), ['HH:mm:ss', 'HH:mm'], true)
  if (!parsedTime.isValid()) return '—'
  return parsedTime.format('HH:mm')
}

const formatDateTime = (value) => {
  if (!value) return '—'
  const parsedDateTime = moment(value)
  if (!parsedDateTime.isValid()) return '—'
  return parsedDateTime.format('DD/MM/YYYY HH:mm')
}

export default function useList() {
  const { t } = useI18nUtils()
  const trans = {
    'messages.error.server_error': t('messages.error.server_error'),
  }

  const refListTable = ref(null)

  const toast = useToast()

  // ---- Options (dispatch stores) ----
  store.dispatch('unit/getAll')
  const unitOptions = computed(() => store.getters['unit/getSelectedItemsSirh'])

  store.dispatch('unit_log/getAll', {active: true})
  const unitLogOptions = computed(() => store.getters['unit_log/getSelectedItems'])

  store.dispatch('unit_commander/getAll')
  const agentOptions = computed(() => store.getters['unit_commander/getSelectedItems'])

  store.dispatch('revision_code/getAll', { max: 50 })
  const revisionCodeOptions = computed(() => store.getters['revision_code/getSelectedItems'])

  // ---- Table ----
  const tableColumns = [
    { label: 'actions.name', key: 'actions', sortable: false },
    { label: 'patrol_logs.unit_log.unit', key: 'unit_id', sortable: true },
    { label: 'unit.sirh', key: 'unit.sirh', sortable: true },
    { label: 'patrol_logs.patrol_log.unit', key: 'unit.description', sortable: true },
    { label: 'patrol_logs.patrol_log.revision_code', key: 'revision_code', sortable: true },
    { label: 'patrol_logs.patrol_log.shipping_time', key: 'shipping_time', sortable: true, formatter: 'formatTimeOnly' },
    { label: 'patrol_logs.patrol_log.arrival_time', key: 'arrival_time', sortable: true, formatter: 'formatTimeOnly' },
    { label: 'patrol_logs.patrol_log.departure_time', key: 'departure_time', sortable: true, formatter: 'formatTimeOnly' },
    { label: 'patrol_logs.patrol_log.review_time', key: 'review_time', sortable: true },
    { label: 'patrol_logs.patrol_log.travel_time', key: 'travel_time', sortable: true },
    { label: 'patrol_logs.patrol_log.command_officer', key: 'unit_log.unit_commander.name', sortable: true },
    { label: 'patrol_logs.unit_commander.telephone_number', key: 'unit_log.unit_commander.telephone_number', sortable: true },
    { label: 'patrol_logs.patrol_log.patrol_officer', key: 'unit_log.patrol_office.name', sortable: true },
    { label: 'patrol_logs.unit_commander.telephone_number', key: 'unit_log.patrol_office.telephone_number', sortable: true },
    { label: 'patrol_logs.patrol_log.revision_code', key: 'revision_code.name', sortable: true },
    { label: 'patrol_logs.patrol_log.comments', key: 'comments', sortable: false },
  ]

  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })

  // ---- Filters ----
  const unitFilter = ref(null)
  const commanderFilter = ref(null)
  const officerFilter = ref(null)
  const revisionCodeFilter = ref(null)
  const filters = ref({})
  const filtersCount = useFilterCounter(filters)

  const buildListQueryParams = () => ({
    q: searchQuery.value,
    max: perPage.value,
    'page[n]': currentPage.value,
    sortBy: sortBy.value,
    sortDesc: isSortDirDesc.value,
    unit: unitFilter.value,
    commander: commanderFilter.value,
    officer: officerFilter.value,
    revision_code: revisionCodeFilter.value,
    with: ['unit_log', 'unit', 'user', 'revision_code'],
  })

  const fetchList = (ctx, callback) => {
    store.dispatch('patrol_log/getAll', buildListQueryParams())
      .then(response => {
        callback(response.data)
        totalList.value = response.meta.total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: trans['messages.error.server_error'],
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const formatDateAssigned = (value) => formatDateTime(value)

  const refetchData = () => {
    refListTable.value.refresh()
  }

  watch([currentPage, perPage], () => {
    refetchData()
  })

  watch([searchQuery], debounce(() => {
    refetchData()
  }, 500))

  const buildFilters = () => {
    filters.value = {
      unit: unitFilter.value,
      commander: commanderFilter.value,
      officer: officerFilter.value,
      revision_code: revisionCodeFilter.value,
    }
    refetchData()
  }

  const clearSearchFilters = () => {
    unitFilter.value = null
    commanderFilter.value = null
    officerFilter.value = null
    revisionCodeFilter.value = null
    filters.value = {}
    buildFilters()
  }

  return {
    refListTable,
    tableColumns,
    perPage,
    currentPage,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    totalList,
    dataMeta,
    fetchList,
    formatDateAssigned,
    formatTimeOnly,
    refetchData,

    unitOptions,
    unitLogOptions,
    agentOptions,
    revisionCodeOptions,

    // filters
    unitFilter,
    commanderFilter,
    officerFilter,
    revisionCodeFilter,
    filtersCount,
    buildFilters,
    clearSearchFilters,
    buildListQueryParams,
  }
}
